import React from "react";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Box, Button, Divider, Grid, TextField, Typography } from "@mui/material";

import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import { notificationAdd } from "../../../../actions/notification";
import { updateFonepayDynamicQrData } from "../../../../actions/resourceCentre";
import {
  IDynamicQrPaymentInfoMetadata,
  IFonepayDynamicQrFormData,
  IFonepayDynamicQrData
} from "../../../../interfaces/QrPayment";

interface FieldConfig<T extends PropertyKey> {
  name: T;
  label: string;
  subLabel: string;
  required: boolean;
  type: string;
  statusKey?: keyof IDynamicQrPaymentInfoMetadata["fonepay"];
  infoText?: (configured: boolean) => string;
}

export const FonepayFields: FieldConfig<keyof IFonepayDynamicQrData>[] = [
  {
    name: "username",
    label: "Username",
    subLabel: "Username provided by Fonepay for user authentication.",
    type: "text",
    required: true
  },
  {
    name: "merchantCode",
    label: "Merchant Code",
    subLabel: "Unique merchant code provided by Fonepay.",
    type: "text",
    required: true
  }
];

export const FonepaySecretFields: FieldConfig<"password" | "secretKey">[] = [
  {
    name: "password",
    label: "Password",
    subLabel: "Password provided by Fonepay for user authentication.",
    type: "text",
    required: false,
    statusKey: "hasPassword",
    infoText: (configured) =>
      configured
        ? "A password is already configured. Enter a new password to replace it."
        : "No password has been configured."
  },
  {
    name: "secretKey",
    label: "Secret Key",
    subLabel: "Secret key provided by Fonepay.",
    type: "text",
    required: false,
    statusKey: "hasSecretKey",
    infoText: (configured) =>
      configured
        ? "A secret key is already configured. Enter a new secret key to replace it."
        : "No secret key has been configured."
  }
];

// eslint-disable-next-line import/prefer-default-export
export function FonepayForm({
  resourceCentre
}: {
  resourceCentre: ResourceCentre;
}): React.ReactNode {
  const dispatch = useDispatch();
  const metadata = resourceCentre?.dynamicQrPaymentInfoMetadata ?? {};

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<IFonepayDynamicQrFormData>({
    defaultValues: {
      username: resourceCentre.dynamicQrPaymentInfo?.fonepay?.username ?? "",
      merchantCode: resourceCentre.dynamicQrPaymentInfo?.fonepay?.merchantCode ?? "",
      password: "",
      secretKey: ""
    }
  });

  const onSubmit = async (data: IFonepayDynamicQrFormData) => {
    if (!resourceCentre?.id) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: "Resource centre ID not found"
        })
      );

      return;
    }

    const payload: Partial<IFonepayDynamicQrFormData> = {
      ...data
    };

    if (!payload.password?.trim()) {
      delete payload.password;
    }

    if (!payload.secretKey?.trim()) {
      delete payload.secretKey;
    }

    try {
      await dispatch(updateFonepayDynamicQrData(Number(resourceCentre?.id), payload));

      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "success",
          autoTimeout: true,
          message: "Fonepay settings updated successfully"
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err?.message || err?.data?.message || "Failed to update settings"
        })
      );
    }
  };

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Typography component="h3" sx={{ fontWeight: "bold" }}>
          Fonepay Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your merchant credentials and business details to enable Fonepay QR payments.
        </Typography>
      </Box>

      <Divider />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          {FonepayFields.map(({ name, label, subLabel, type, infoText, statusKey }) => (
            <React.Fragment key={name}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body1" component="label" htmlFor={name}>
                  {label}
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  {subLabel}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    const secretValue = statusKey ? metadata?.fonepay?.[statusKey] : false;

                    return (
                      <TextField
                        {...field}
                        fullWidth
                        id={name}
                        type={type}
                        error={!!errors[name]}
                        variant="outlined"
                        placeholder={label}
                        label={label}
                        onFocus={(e) => e.target.select()}
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText={errors[name]?.message ?? infoText?.(secretValue)}
                      />
                    );
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}

          {/* secrets */}
          {FonepaySecretFields.map(({ name, label, subLabel, type, infoText, statusKey }) => (
            <React.Fragment key={name}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body1" component="label" htmlFor={name}>
                  {label}
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  {subLabel}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    const secretValue = statusKey ? metadata?.fonepay?.[statusKey] : false;

                    return (
                      <TextField
                        {...field}
                        fullWidth
                        id={name}
                        type={type}
                        error={!!errors[name]}
                        variant="outlined"
                        placeholder={label}
                        label={label}
                        onFocus={(e) => e.target.select()}
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText={errors[name]?.message ?? infoText?.(secretValue)}
                      />
                    );
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}

          <Box sx={{ display: "flex", ml: "auto" }}>
            <Button type="submit" variant="contained" color="primary" sx={{ ml: "auto" }}>
              Save Fonepay Configuration
            </Button>
          </Box>
        </Grid>
      </form>
    </Box>
  );
}
